import { useForm, zodResolver } from "@mantine/form";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { ContractionsFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/components/ContractionsFormFields";
import {
  CONTRACTIONS_DATE_LINK_ID,
  CONTRACTIONS_DATE_TIME_LINK_ID,
  CONTRACTIONS_DURATION_LINK_ID,
  CONTRACTIONS_FREQUENCY_LINK_ID,
  CONTRACTIONS_QUESTIONNAIRE_NAME,
  CONTRACTIONS_ROOT_LINK_ID,
  CONTRACTIONS_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/constants/contractions-link-ids";
import type { ContractionsFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/types/contractions-drawer";
import type { ContractionsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/types/contractions-form";
import {
  getDefaultContractionsFlowsheetFormValues,
  normalizeContractionsFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/utils/contractions-form-defaults";
import {
  mapContractionsFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToContractionsFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/utils/contractions-questionnaire-mapping";
import { contractionsFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/zod-schemas/contractions-flowsheet-form";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import {
  type LaborFlowsheetDrawerEditContext,
  useLaborFlowsheetDrawerPersist,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";

const CONTRACTIONS_DISPLAY_NAME = LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Contractions];

/**
 * Builds edit-mode persist context from drawer props; undefined for quick-add.
 */
function getContractionsEditContext(
  props: ContractionsFlowsheetDrawerProps,
): LaborFlowsheetDrawerEditContext | undefined {
  if (props.mode !== "edit") {
    return undefined;
  }

  return {
    observation: props.observation,
    questionnaireDisplayName: props.questionnaireDisplayName,
  };
}

export function mergeContractionsHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: ContractionsFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): ContractionsFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[CONTRACTIONS_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[CONTRACTIONS_DATE_TIME_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedContractionDate = hydratedDateTime?.[CONTRACTIONS_DATE_LINK_ID];
  const hydratedContractionTime = hydratedDateTime?.[CONTRACTIONS_TIME_LINK_ID];
  const hydratedFrequency = hydratedRoot?.[CONTRACTIONS_FREQUENCY_LINK_ID];
  const hydratedDuration = hydratedRoot?.[CONTRACTIONS_DURATION_LINK_ID];

  return {
    ...mapped,
    contractionDate:
      hydratedContractionDate instanceof Date ? hydratedContractionDate : mapped.contractionDate,
    contractionTime:
      typeof hydratedContractionTime === "string"
        ? hydratedContractionTime
        : mapped.contractionTime,
    frequency:
      typeof hydratedFrequency === "string" || typeof hydratedFrequency === "number"
        ? hydratedFrequency
        : mapped.frequency,
    duration:
      typeof hydratedDuration === "string" || typeof hydratedDuration === "number"
        ? hydratedDuration
        : mapped.duration,
  };
}

export function ContractionsFlowsheetDrawer(props: ContractionsFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;

  const form = useForm<ContractionsFlowsheetFormValues>({
    initialValues: getDefaultContractionsFlowsheetFormValues(),
    validate: zodResolver(contractionsFlowsheetFormSchema),
  });

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<ContractionsFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: CONTRACTIONS_QUESTIONNAIRE_NAME,
    displayName: CONTRACTIONS_DISPLAY_NAME,
    timerKey: "contractions",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeContractionsFlowsheetFormValues(contractionsFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultContractionsFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapContractionsFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToContractionsFormValues,
    mergeHydratedEditFormValues: mergeContractionsHydratedEditFormValues,
    questionnaireNotFoundMessage: "Contractions questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing contractions entry.",
    editContext: getContractionsEditContext(props),
    onRefresh: mode === "quick-add" ? props.onRefresh : undefined,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${QuestionnaireType.Contractions}`}
      submitButtonText={mode === "edit" ? "Update" : "Add To Flowsheet"}
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <ContractionsFormFields form={form} mode={mode} clientId={clientId} isDrawerOpened={opened} />
    </LaborFlowsheetDrawerShell>
  );
}
